import { useState, useEffect } from "react";
import HealthIcon from "./Icons/HealthIcon";
import SpeedIcon from "./Icons/SpeedIcon";
import DamageIcon from "./Icons/DamageIcon";
import {
  calculateAuraCost,
  calculateProbability,
  calculateStats,
  formatNumber,
  getRandomNumber,
} from "./Functions";
import MainIcon from "./Icons/MainIcon";
import SellOneIcon from "./Icons/SellOneIcon";
import SellAllIcon from "./Icons/SellAllIcon";
import UpgradeIcon from "./Icons/UpgradeIcon";
import "./UpgradeStyles.css";
import auraBgList from "./Backgrounds";
import { UpgradeWindow } from "./UpgradeWindow";
import ConfirmMessage from "./ConfirmMessage";
import ReloadIcon from "./Icons/ReloadIcon";
import BpIcon from "./Icons/BpIcon";
import { useTooltip } from "./useTooltip";
import Tooltip from "./Tooltip";

const ActiveItem = ({
  itemsList,
  activeItem,
  setActiveItem,
  motion,
  handleItemRemove,
  handleEquipmentUpdate,
  changeMainAura,
  handleSell,
  t,
  handleAurasUpdate,
  currentAuraTitle,
  localProfile,
  farm,
  changeDealDucks,
  updateLocalItemsData,
}) => {
  const [isImgLoaded, setIsImgLoaded] = useState(false);
  const [auraStats, setAuraStats] = useState({});
  const [auraProbability, setAuraProbability] = useState(null);
  const [upgradeWindow, setUpgradeWindow] = useState(false);

  const { tooltip, showTooltip, tooltipRef } = useTooltip();

  useEffect(() => {
    if (activeItem?.type === "aura") {
      setAuraStats(calculateStats(activeItem));
      setAuraProbability(
        formatNumber(
          calculateProbability(activeItem.probability, activeItem.auraLvl)
        )
      );
    }
  }, [localProfile.AurasList, activeItem]);

  const [auraPrice, setAuraPrice] = useState(0);

  const [isConfirmBgOpen, setIsConfirmBgOpen] = useState(false);
  const [isConfirmSellOneOpen, setIsConfirmSellOneOpen] = useState(false);
  const [isConfirmSellAllOpen, setIsConfirmSellAllOpen] = useState(false);

  const handleConfirmChangeBg = async () => {
    setIsConfirmBgOpen(false);
    const success = await changeDealDucks(-10, "Change aura BG");
    const newBg = getRandomNumber(0, 27);
    if (success) {
      await handleAurasUpdate(
        {
          title: activeItem.title,
          probability: activeItem.probability,
          quantity: 0,
          auraLvl: activeItem.auraLvl,
          auraBg: newBg,
        },
        null
      );
    }
  };

  const handleConfirmSellOne = async () => {
    setIsConfirmSellOneOpen(false);
    handleSell(activeItem, 1);
  };

  const handleConfirmSellAll = async () => {
    setIsConfirmSellAllOpen(false);
    handleSell(activeItem, activeItem.quantity - 1);
  };

  const changeBg = async () => {
    setIsConfirmBgOpen(true);
  };

  const renderButtons = () => {
    switch (activeItem?.type) {
      case "ring":
      case "glove":
      case "necklace":
      case "helmet":
        return (
          <motion.button
            whileTap={{ scale: 0.97 }}
            transition={{ duration: 0.1 }}
            className="zoom-item-button-center"
            onClick={() =>
              handleEquipmentUpdate(
                activeItem,
                activeItem.equipped ? "unequip" : "equip"
              )
            }
          >
            {activeItem.equipped
              ? t("take off")
              : t("Put") + " " + t(activeItem.type)}
          </motion.button>
        );
      case "potion":
        return (
          <>
            <motion.button
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.1 }}
              className="zoom-item-button"
              onClick={() => handleItemRemove(activeItem, 1)}
            >
              {t("use")}
            </motion.button>
            {activeItem.quantity > 1 && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.1 }}
                className="zoom-item-button"
                onClick={() =>
                  handleItemRemove(activeItem, activeItem.quantity)
                }
              >
                {t("use all")}
              </motion.button>
            )}
          </>
        );
      case "aura":
        return (
          <>
            <motion.button
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.1 }}
              className="zoom-item-button"
              disabled={currentAuraTitle === activeItem.title}
              onClick={() => changeMainAura(activeItem)}
            >
              <div>{t("main")}</div>
              <MainIcon size="36px" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.1 }}
              className="zoom-item-button"
              style={{
                backgroundImage:
                  "linear-gradient(45deg,rgb(252, 188, 249),rgb(204, 176, 240), #FFCC70)",
              }}
              onClick={() => setUpgradeWindow(true)}
            >
              <div>{t("Upgrade")}</div>
              <UpgradeIcon size="36px" />
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.97 }}
              transition={{ duration: 0.1 }}
              className="zoom-item-button"
              onClick={() => {
                setAuraPrice(calculateAuraCost(activeItem));
                setIsConfirmSellOneOpen(true);
              }}
            >
              <div>{t("sell one")}</div>
              <SellOneIcon size="36px" />
            </motion.button>
            {activeItem.quantity > 1 && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.1 }}
                className="zoom-item-button"
                onClick={() => {
                  setAuraPrice(
                    calculateAuraCost(activeItem) * (activeItem.quantity - 1)
                  );
                  setIsConfirmSellAllOpen(true);
                }}
              >
                <div>{t("sell all")}</div>
                <SellAllIcon size="36px" />
              </motion.button>
            )}
            {activeItem?.auraLvl >= 17 && (
              <motion.button
                whileTap={{ scale: 0.97 }}
                transition={{ duration: 0.1 }}
                className="zoom-item-button"
                onClick={changeBg}
              >
                <div>{t("Change BG")}</div>
                <ReloadIcon size="32px" />
              </motion.button>
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {tooltip && (
        <Tooltip
          text={tooltip.text}
          position={tooltip.position}
          tooltipRef={tooltipRef}
        />
      )}
      {isConfirmBgOpen && (
        <ConfirmMessage
          message={t(
            "Are you sure you want to change the background for 10 DealDUCK$?"
          )}
          onConfirm={handleConfirmChangeBg}
          onCancel={() => setIsConfirmBgOpen(false)}
        />
      )}
      {isConfirmSellOneOpen && (
        <ConfirmMessage
          message={
            t(`Are you sure you want to sell 1 pc. for`) +
            " " +
            auraPrice +
            ` DUCK$?`
          }
          onConfirm={handleConfirmSellOne}
          onCancel={() => setIsConfirmSellOneOpen(false)}
        />
      )}
      {isConfirmSellAllOpen && (
        <ConfirmMessage
          message={
            t(`Are you sure you want to sell all but one for`) +
            " " +
            auraPrice +
            " DUCK$?"
          }
          onConfirm={handleConfirmSellAll}
          onCancel={() => setIsConfirmSellAllOpen(false)}
        />
      )}
      {upgradeWindow && (
        <UpgradeWindow
          {...{
            t,
            auraStats,
            motion,
            localProfile,
            handleAurasUpdate,
            itemsList,
            setUpgradeWindow,
            activeItem,
            updateLocalItemsData,
          }}
        />
      )}
      <motion.div
        initial={{ scale: 0.7 }}
        animate={{ scale: 1 }}
        className="zoom-item-wrapper"
      >
        <div
          className="zoom-item-container upgrade-background"
          style={{
            backgroundColor: activeItem?.color,
            backgroundImage: `url(${auraBgList[activeItem.auraBg]?.auraBg})`,
          }}
        >
          <div
            className="current-name-container"
            style={{
              color:
                activeItem?.type !== "aura"
                  ? "rgba(255, 255, 255, 0.83)"
                  : activeItem.color,
              backgroundColor:
                activeItem?.type !== "aura"
                  ? "rgba(0, 0, 0, 0.4)"
                  : "rgba(0,0,0,1)",
            }}
          >
            <div className="zoom-item-title">
              {t(activeItem?.title) +
                (activeItem?.auraLvl > 0 ? ` +${activeItem.auraLvl}` : "")}
            </div>
            {activeItem?.type === "aura" && (
              <div
                onClick={(e) =>
                  showTooltip(
                    e,
                    t(`This aura appears 1 time out of`) + " " + auraProbability
                  )
                }
                className="current-rarity"
                style={{ backgroundColor: activeItem.color, color: "black" }}
              >
                {"1/" + auraProbability}
              </div>
            )}
          </div>
          {activeItem?.quantity && (
            <div className="zoom-item-quantity">
              {activeItem.quantity} {t("pcs.")}
            </div>
          )}
          {activeItem?.rarity && (
            <div
              className="zoom-item-rarity"
              style={{ backgroundColor: activeItem.rarityColor }}
            >
              {t(activeItem.rarity)}
            </div>
          )}
          <div
            className="shimmer"
            style={{ display: isImgLoaded ? "none" : "block" }}
          ></div>
          {activeItem?.webm ? (
            <video
              className="zoom-item-img "
              src={activeItem.img}
              onLoadedData={() => setIsImgLoaded(true)}
              autoPlay
              loop
              muted
              style={{ display: isImgLoaded ? "block" : "none" }}
            />
          ) : (
            <img
              className="zoom-item-img"
              src={activeItem?.img}
              onLoad={() => setIsImgLoaded(true)}
              style={{ display: isImgLoaded ? "block" : "none" }}
              alt="active-item"
            />
          )}
          {activeItem?.type === "aura" && (
            <div
              style={{
                position: "absolute",
                bottom: -3,
                left: 0,
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "relative",
                  display: "flex",
                  gap: 16,

                  justifyContent: "space-around",
                  backgroundColor: "rgb(0, 0, 0)",
                  padding: "6px 20px",
                  borderRadius: "30px 30px 0 0",
                  fontSize: 12,
                }}
              >
                <div
                  onClick={(e) => showTooltip(e, t("Health"))}
                  style={{ display: "flex", gap: 5, alignItems: "center" }}
                >
                  <HealthIcon size="12px" fill="red" />
                  <div>{formatNumber(auraStats?.hp)}</div>
                </div>
                <div
                  onClick={(e) =>
                    showTooltip(e, t("Number of hits per second"))
                  }
                  style={{ display: "flex", gap: 5, alignItems: "center" }}
                >
                  <SpeedIcon size="12px" fill="green" />
                  <div>{formatNumber(auraStats?.speed)}/sec</div>
                </div>
                <div
                  onClick={(e) =>
                    showTooltip(e, t("The amount of damage per hit"))
                  }
                  style={{ display: "flex", gap: 5, alignItems: "center" }}
                >
                  <DamageIcon size="12px" fill="orange" />
                  <div>{formatNumber(auraStats?.damage)}</div>
                </div>
                <div
                  onClick={(e) =>
                    showTooltip(
                      e,
                      t(
                        "Battle power is the total strength of your aura, taking into account health, attack speed, and damage"
                      )
                    )
                  }
                  style={{ display: "flex", gap: 5, alignItems: "center" }}
                >
                  <BpIcon size="12px" fill="yellow" />
                  <div>{formatNumber(auraStats?.bp)}</div>
                </div>
              </div>
            </div>
          )}
          {activeItem.type !== "aura" && (
            <div className="active-item-description">
              {activeItem?.type === "craft" && (
                <span>{t("need for crafting")}</span>
              )}
              {activeItem?.luckBonus > 0 && (
                <span>
                  {t("base luck")} +{activeItem?.luckBonus} <br></br>
                </span>
              )}
              {activeItem?.speedBonus > 0 && activeItem.type === "necklace" && (
                <span>
                  {t("spin time")} -{activeItem?.speedBonus / 1000} {t("sec")}
                  <br></br>
                </span>
              )}

              {activeItem.description && (
                <span style={{ lineHeight: "1.1em" }}>
                  {t(activeItem.description)}
                </span>
              )}
              {!activeItem.description && activeItem.type === "helmet" && (
                <span>
                  {t("bonus luck")} {t("every")} {activeItem?.spinsCount}{" "}
                  {t("spins")}
                  <br></br>
                </span>
              )}
              {!activeItem.description && activeItem.type === "glove" && (
                <span>
                  {t("bonus luck")} {activeItem?.bonusSpinLuck}
                  <br></br>
                </span>
              )}
              {activeItem?.multiplyer > 0 && activeItem.type === "potion" && (
                <div>
                  {t("luck")} +{activeItem.multiplyer} <br />
                  {t("duration")} {activeItem.timer} {t("min")}
                </div>
              )}
              {activeItem?.speedBonus > 0 && activeItem.type === "potion" && (
                <div>
                  {t("spin time")} -{activeItem.speedBonus / 1000} {t("sec")}{" "}
                  <br />
                  {t("duration")} {activeItem.timer} {t("min")}
                </div>
              )}
            </div>
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
            gap: "10px",
            width: "100%",
            justifyContent: "center",
            gridAutoFlow: "dense",
          }}
        >
          {renderButtons()}
        </div>
      </motion.div>
      <div
        onClick={() => setActiveItem(null)}
        className="black-screen"
        style={{ backgroundColor: "#000000c4", zIndex: 4 }}
      ></div>
    </>
  );
};

export default ActiveItem;
